import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useAuthenticationState } from '~/atoms/AuthenticationState'
import { useMangoApiClient } from '~/atoms/MangoApi'
import Logger from '~/services/Logger'
import { getUserFromToken } from '~/utils/auth'

import {
  ApiError,
  PaginatedApiResult,
  SessionUser,
  User,
  UserRoles,
  VoipProfile,
  VoipProfileSubjectType,
} from '@mangovoice/types'
import { ExtensionInstance, Pbx } from '@mangovoice/types'

const MAX_PAGINATED_RESULTS = 999

interface UserSessionDependencyData {
  pbx: Pbx
  userExtensions: ExtensionInstance[]
  pbxUsers: User[]
  userRoles?: UserRoles
  sessionUser?: SessionUser
  botProfiles?: VoipProfile[]
}
interface UseGetUserSessionDependenciesParams {
  onQuerySuccess?: (data: UserSessionDependencyData) => void
  onQueryErrored?: (pbxId: number, error: ApiError) => void
  options?: UseQueryOptions<UserSessionDependencyData, ApiError>
}
const logger = new Logger('useGetUserSessionDependencies')
export const useGetUserSessionDependencies = ({
  onQuerySuccess,
  onQueryErrored,
  options,
}: UseGetUserSessionDependenciesParams) => {
  const api = useMangoApiClient()
  // Only `.id` is needed to refetch — the full PBX is fetched fresh from the
  // API below. Storing a wider shape lets callers pass either a full Pbx or
  // a lightweight summary without an unsafe cast.
  const [selectedPbx, setSelectedPbx] = useState<Pick<Pbx, 'id'> | null>(null)
  const { authenticationState } = useAuthenticationState()

  const query = useQuery<UserSessionDependencyData, ApiError>({
    queryKey: ['userSessionDependencies', selectedPbx?.id ?? 0],
    queryFn: async () => {
      const { id: selectedPbxId } = selectedPbx ?? {}
      if (!selectedPbxId) {
        const error = { message: 'No pbx selected' }
        onQueryErrored?.(0, error)
        return Promise.reject(error)
      }

      // Fetch full pbx details to ensure we have all fields partner fields
      const {
        ok: pbxOk,
        data: pbxResult,
        status: pbxStatus,
      } = await api.pbxes.getPbx(selectedPbxId)

      if (!pbxOk || !pbxResult) {
        const error: ApiError = {
          ...(pbxResult as object),
          status: pbxStatus,
        }
        onQueryErrored?.(selectedPbxId, error)
        return Promise.reject(error)
      }

      //fetch user extension data
      const {
        ok: userExtensionsOk,
        data: userExtensionsResult,
        status: userExtensionsStatus,
      } = await api.extensions.listLegacy({
        pbx_id: selectedPbxId,
        type: 'ExtensionLine',
        limit: MAX_PAGINATED_RESULTS,
        ordering: 'name',
      })
      logger.info('User Extensions Result', {
        userExtensionsOk,
        userExtensionsResult,
        userExtensionsStatus,
      })

      if (!userExtensionsOk || !userExtensionsResult) {
        const error: ApiError = {
          ...(userExtensionsResult as object),
          status: userExtensionsStatus,
        }
        onQueryErrored?.(selectedPbxId, error)
        return Promise.reject(error)
      }

      const useQueryParams = {
        pbx_id: selectedPbxId,
        limit: MAX_PAGINATED_RESULTS,
      }

      const {
        ok: pbxUsersOk,
        data: pbxUsersResult,
        status: pbxUsersStatus,
      } = await api.users.listSelect(useQueryParams)

      if (!pbxUsersOk || !pbxUsersResult) {
        const error: ApiError = {
          ...(pbxUsersResult as object),
          status: pbxUsersStatus,
        }
        onQueryErrored?.(selectedPbxId, error)
        return Promise.reject(error)
      }

      const sessionUser = getUserFromToken(
        authenticationState.appSessionJwts?.access_token,
      )

      let userRoles
      if (sessionUser?.id) {
        const {
          ok: userRolesOk,
          data: userRolesResult,
          status: userRolesStatus,
        } = await api.users.getUserRoles(sessionUser.id)

        if (!userRolesOk || !userRolesResult) {
          const error: ApiError = {
            ...(userRolesResult as object),
            status: userRolesStatus,
          }
          onQueryErrored?.(selectedPbxId, error)
          return Promise.reject(error)
        }
        userRoles = userRolesResult
      }

      // Fetch bot profiles (non-blocking - we don't fail if this errors)
      let botProfiles: VoipProfile[] = []
      try {
        const response = await api.voipProfiles.list({
          subject_type: VoipProfileSubjectType.Bot,
          pbx_id: selectedPbxId,
          limit: MAX_PAGINATED_RESULTS,
        })
        const botProfilesResult = response.data as
          PaginatedApiResult<VoipProfile> | undefined
        if (response.ok && botProfilesResult?.results) {
          botProfiles = botProfilesResult.results
        }
      } catch {
        // Silently ignore - bot profiles are optional
      }

      const result = {
        pbx: pbxResult,
        pbxUsers: pbxUsersResult.results,
        userExtensions: userExtensionsResult.results,
        userRoles,
        sessionUser,
        botProfiles,
      }
      onQuerySuccess?.(result)
      return result
    },
    ...options,
    enabled: false,
    retry: false,
  })

  const refetch = (pbx: Pick<Pbx, 'id'>) => {
    setSelectedPbx(pbx)
  }

  useEffect(() => {
    if (!selectedPbx) {
      return
    }

    query.refetch()
  }, [selectedPbx])

  return {
    ...query,
    refetch,
  }
}
